import { getBaseURL } from "../utilities/parseCookie";
import { getOverrideAccountID } from "../context/auth";
import { getEnvironment } from "../utilities/environment";

export let baseURL = "https://api.kahaniapp.com";

export const determineBaseURL = () => {
  let url = baseURL;

  // Check for an API URL match based on hostname first, then use the default for the environment
  switch (window.location.hostname) {
    case "m1t.local":
      url = "http://m1t.local:5001";
      break;
    case "localhost":
      url = "http://localhost:5001";
      break;
    case "kahani-dev-adam.ngrok.io":
      url = "https://kahani-dev-adam-api.ngrok.io";
      break;
    case "kahani-dev-brian.ngrok.io":
      url = "https://api.dev.kahaniapp.com";
      break;
    default:
      // Check the current environment to define the API URL
      switch (getEnvironment()) {
        case "localhost":
          url = "http://localhost:5001";
          break;
        case "development":
          url = "https://api.dev.kahaniapp.com";
          break;
        case "pull_request":
          url = "https://api.dev.kahaniapp.com";
          break;
        default:
          url = "https://api.kahaniapp.com";
      }
  }

  // Finally, check for a cookie that will switch to a different API
  const customBaseURL = getBaseURL();
  if (customBaseURL) {
    url = customBaseURL;
  }

  return url;
};

baseURL = determineBaseURL();

const accountSlug = (accountID = "") => {
  // If an override account ID is set, use that
  const overrideAccountID = getOverrideAccountID();
  if (accountID) {
    return `accounts/${accountID}`;
  } else if (overrideAccountID) {
    return `accounts/${overrideAccountID}`;
  } else {
    // otherwise, the API will use the token
    return "account";
  }
};

export const shopifyOAuthURLs = {
  authorize: () => {
    return `${baseURL}/v0/public/shopify/oauth/authorize`;
  },
  callback: () => {
    return `${baseURL}/v0/public/shopify/oauth/callback`;
  },
};

export const instagramOAuthURLs = {
  authorize: () => {
    return `${baseURL}/v0/${accountSlug()}/instagram/oauth/authorize`;
  },
  callback: () => {
    return `${baseURL}/v0/${accountSlug()}/instagram/oauth/callback`;
  },
  setBusinessAccountID: () => {
    return `${baseURL}/v0/${accountSlug()}/instagram/businessAccountID`;
  },
  getBusinessAccounts: () => {
    return `${baseURL}/v0/${accountSlug()}/instagram/accounts`;
  },
};

export const influencerURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/influencers`;
  },
  details: (influencerID) => {
    return `${baseURL}/v0/${accountSlug()}/influencers/${influencerID}`;
  },
  scrapeIGProfilePicture: (handle) => {
    return `${baseURL}/v0/${accountSlug()}/instagram/${handle}/profilePicture`;
  },
};

export const microshopURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/microshops`;
  },
  details: (microshopID) => {
    return `${baseURL}/v0/${accountSlug()}/microshops/${microshopID}`;
  },
  microshopHandleAvailable: (handle, microshopID) => {
    return `${baseURL}/v0/${accountSlug()}/microshopHandleAvailable?handle=${handle}&microshop_id=${microshopID}`;
  },
};

export const collectionURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/collections`;
  },
  details: (collectionID) => {
    return `${baseURL}/v0/${accountSlug()}/collections/${collectionID}`;
  },
  storyOrder: (collectionID) => {
    return `${baseURL}/v0/${accountSlug()}/collections/${collectionID}/setStories`;
  },
  publish: (collectionID) => {
    return `${baseURL}/v0/${accountSlug()}/collections/${collectionID}/publish`;
  },
  duplicate: (collectionID) => {
    return `${baseURL}/v0/${accountSlug()}/collections/${collectionID}/duplicate`;
  },
  publicCollection: (collectionID, accountID) => {
    return `${baseURL}/v0/public/accounts/${accountID}/collections/${collectionID}`;
  },
  previewURL: (collectionURL, accountID) => {
    // Base64 encode the collection URL
    collectionURL = encodeURIComponent(collectionURL);

    return `${baseURL}/v0/public/accounts/${accountID}/functions/collectionPreview?collectionURL=${collectionURL}`;
  },
  livePreviewUpdate: (token) => {
    return `${baseURL}/v0/${accountSlug()}/functions/livePreviewUpdate/${token}`;
  },
  storyMaker: () => {
    return `${baseURL}/v0/${accountSlug()}/storyMaker`;
  },
};

export const shopifyDataURLs = {
  shopifySyncStatus: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify/data/scheduler/latestJob`;
  },
  shopifyCollectionList: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify/data/query/collections`;
  },
  shopifyProductList: (deep = false) => {
    if (deep) {
      return `${baseURL}/v0/${accountSlug()}/shopify/data/query/products?deep=true`;
    } else {
      return `${baseURL}/v0/${accountSlug()}/shopify/data/query/products`;
    }
  },
  shopifyProduct: (product_id) => {
    return `${baseURL}/v0/${accountSlug()}/shopify/data/query/products/${product_id}`;
  },
};

export const storyURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/stories`;
  },
  details: (storyID) => {
    return `${baseURL}/v0/${accountSlug()}/stories/${storyID}`;
  },
  chapterOrder: (storyID) => {
    return `${baseURL}/v0/${accountSlug()}/stories/${storyID}/setChapters`;
  },
};

export const chapterURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/chapters`;
  },
  details: (chapterID) => {
    return `${baseURL}/v0/${accountSlug()}/chapters/${chapterID}`;
  },
};

export const assetURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/assets`;
  },
  tags: () => {
    return `${baseURL}/v0/${accountSlug()}/functions/assetTagList`;
  },
  upload: () => {
    return `${baseURL}/v0/${accountSlug()}/assets`;
  },
  asset: (assetID) => {
    return `${baseURL}/v0/${accountSlug()}/assets/${assetID}`;
  },
  crop: (assetID) => {
    return `${baseURL}/v0/${accountSlug()}/assets/${assetID}/crop`;
  },
  urlUpload: (useMux) => {
    if (useMux) {
      return `${baseURL}/v0/${accountSlug()}/assets?uploadcare=true&useMux=true`;
    } else {
      return `${baseURL}/v0/${accountSlug()}/assets?uploadcare=true`;
    }
  },
  downloadURL: (assetID) => {
    return `${baseURL}/v0/${accountSlug()}/assets/${assetID}/download`;
  },
};

export const ctaURLs = {
  list: () => {
    return `${baseURL}/v0/${accountSlug()}/CTAs`;
  },
  cta: (ctaID) => {
    return `${baseURL}/v0/${accountSlug()}/CTAs/${ctaID}`;
  },
};

export const adminURLs = {
  list: () => {
    return `${baseURL}/v0/admin/accounts`;
  },
  salesDemoGenerateCollection: () => {
    return `${baseURL}/v0/public/salesDemos/storyMaker/generate`;
  },
};

export const accountURLs = {
  account: (accountID = "") => {
    return `${baseURL}/v0/${accountSlug(accountID)}`;
  },
  klaviyoLists: () => {
    return `${baseURL}/v0/${accountSlug()}/functions/klaviyoLists`;
  },
  setKlaviyoAPIKey: () => {
    return `${baseURL}/v0/${accountSlug()}/setKlaviyoAPIKey`;
  },
  setGA4OptIn: () => {
    return `${baseURL}/v0/${accountSlug()}/setGA4OptIn`;
  },
  changePassword: () => {
    return `${baseURL}/v0/${accountSlug()}/setPassword`;
  },
  pageTargeting: () => {
    return `${baseURL}/v0/${accountSlug()}/pageTargeting`;
  },
  pageTargetingURLTest: () => {
    return `${baseURL}/v0/${accountSlug()}/pageTargeting/testURLs`;
  },
  uploadThumbnail: () => {
    return `${baseURL}/v0/${accountSlug()}/uploadThumbnail`;
  },
  kvStore: () => {
    return `${baseURL}/v0/${accountSlug()}/kv_store`;
  },
  login: () => `${baseURL}/v0/public/generateToken`,
};

export const shopifyURLs = {
  shopifyStore: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify`;
  },
  verifyAppBlockInstallation: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify/theme/installed`;
  },
  verifyAppBlockSupport: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify/theme/support`;
  },
  redeemPlanCode: () => {
    return `${baseURL}/v0/${accountSlug()}/shopify/plans`;
  },
  validateCharge: (charge_id) => {
    return `${baseURL}/v0/${accountSlug()}/shopify/charges/${charge_id}`;
  },
};
